/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable camelcase */
import classNames from 'classnames'
import React, { FC, useState, ReactNode, useEffect, MouseEventHandler, Fragment } from 'react'

import { isShowingTimeLeft, dateDiffInSeconds } from '../deal/utils/date'
import ExpertPickWrapper from '../expert-pick'
import { DealLink } from '../helpers/deal-link'
import { isTrustpilotScoreAbove4 } from '../pages/hull/utils/postcode'
import TransitionOut from '../transition-out'
import { TrustpilotComponent } from '../trustpilot'
import { HighlightReview } from '../trustpilot/highlight-review'
import { HighSpeedIcon } from '~/assets/icons'
import { generatePriceDetails } from '~/components/deal/utils/price-details'
import generateProviderImage from '~/components/deal/utils/provider-image'
import { DetailsModal } from '~/components/details-modal'
import skeletonStyles from '~/components/skeletons/skeleton-styles.module.css'
import Tabs from '~/components/tabs/'
import { useEcommerceContext } from '~/contexts/ecommerce-context'
import { usePageContext } from '~/contexts/page-context'
import { FragmentType } from '~/gql'
import { Deal, Promotion } from '~/gql/graphql'
import AdFeedFragment from '~/graphql/fragments/ad-fields'
import { useInBucket } from '~/hooks/use-in-bucket'
import { useDealPromotions } from '~/hooks/useDealPromotions'
import { SegmentProps } from '~/interfaces/types'
import { trackSegment } from '~/utils/tracking'

import { Extension, Title, DetailBoxes, Usps, Cta, Image } from './components'
import DealFeatures from './components/deal-features'
import MoreInfoSlideOut from './components/more-info-slide-out'
import { getContent } from './components/more-info-slide-out/content'
import styles from './styles.module.css'

export interface DealCardProps {
  deal: Deal
  tenancy?: FragmentType<typeof AdFeedFragment>
  list: string
  position: number
  adType?: string
  placementType: string
  href: string
  onClick: MouseEventHandler
  isSponsored?: boolean
  segmentProps?: SegmentProps
  asMobileCard?: boolean
  isTvHomepage?: boolean
  hasUspAnimated?: boolean
  setHasUspAnimated?: (hasUspAnimated: boolean) => boolean
  criticalJs?: boolean
  dealLinkRef?: React.RefObject<HTMLDivElement>
  isSkeleton?: boolean
  cityFibreDealsAvailable?: boolean
}

const shouldShowTrustpilotScore = (
  isMs3BannerTest: boolean,
  isCityFibreTest: boolean,
  isPriceRise2024: boolean,
  deal: Deal
) => {
  // show Youfibre scores on all results pages
  if (deal.provider.name === 'Youfibre' && isTrustpilotScoreAbove4(deal.trustScores?.trustScore)) return true

  if (
    isMs3BannerTest && // If the user is in the experiment
    deal.services.broadband?.networkName === 'ms3' && // If the deal is an MS3 deal
    isTrustpilotScoreAbove4(deal.trustScores?.trustScore) // If the trust score is above 4
  )
    return true

  if (
    isCityFibreTest && // If the user is in the experiment
    deal.services.broadband?.networkName === 'cityfibre-trial' && // If the deal is a CityFibre deal
    isTrustpilotScoreAbove4(deal.trustScores?.trustScore) // If the trust score is above 4
  )
    return true

  if (isPriceRise2024 && (deal.provider.id === '12774' || deal.provider.id === '12835')) return true // HyperOptic and Community Fibre

  return false
}

const DealCard: FC<DealCardProps> = ({
  deal,
  tenancy = {},
  list,
  position,
  adType = '',
  placementType,
  href,
  onClick,
  asMobileCard = false,
  isTvHomepage = false,
  hasUspAnimated,
  setHasUspAnimated,
  criticalJs = false,
  isSponsored = false,
  segmentProps = { product: {}, promotion: {} },
  dealLinkRef,
  isSkeleton = false,
  cityFibreDealsAvailable = false,
  ...props
}) => {
  const [showTrustpilotScore, setShowTrustpilotScore] = useState(false)
  const { comparisonId, metadata } = useEcommerceContext()?.data || {}

  const { pageId } = usePageContext()

  const isMs3BannerTest = useInBucket('experiment-trustpilothighlightreview', 'variant')
  const isCityFibreTest = useInBucket('experiment-broadband-city-fibre-trial', 'variant')
  const isPriceRise2024 = !useInBucket('experiment-broadband-price-rise-2024', 'no-price-rises')

  useEffect(() => {
    setShowTrustpilotScore(shouldShowTrustpilotScore(isMs3BannerTest, isCityFibreTest, isPriceRise2024, deal))
  }, [])

  const isTenancyUpdate = useInBucket('experiment-broadband-tenancy-update-p1-p4', `${placementType}-variant`)

  const isP4Standard = useInBucket('experiment-broadband-tenancy-update-p1-p4', 'p4-variant-standard')

  const isBlackFriday = useInBucket('experiment-black-friday-2023', 'variant')

  const simplifySmallPrint = useInBucket('experiment-broadband-asa-contract-pricing-extended', 'lightweight')

  const isMidContractPriceRiseTest = useInBucket('experiment-mid-contract-price-rise-details-badge', 'variant')

  if (simplifySmallPrint) {
    deal.provider.contractTerms.priceRiseText = 'monthly cost may rise during contract'
  }

  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [modalType, setModalType] = useState('')
  const [isMoreInfoOpen, setIsMoreInfoOpen] = useState(false)
  const [isInitialRender, setIsInitialRender] = useState(true)

  useEffect(() => {
    // Used to observe the first render of the component
    // If another state changes, add it here to prevent the
    // `Product Viewed` event from firing on subsequent renders
    if (!isInitialRender || !isMoreInfoOpen) return

    setIsInitialRender(false)
  }, [isMoreInfoOpen])

  const { provider, promotions, endDate, customerAvailability } = deal

  const isExclusive = Object.values(promotions).some((promotion) => promotion.uSwitchExclusive)
  const hasBlackFridayPromotion = Object.values(promotions).some((promotion) => promotion.blackFriday)
  const isEndingSoon = !!(endDate && !!isShowingTimeLeft(dateDiffInSeconds(new Date(), new Date(endDate)) as number))
  const isExtensionShown = !!(isSponsored || isExclusive || isEndingSoon || (hasBlackFridayPromotion && isBlackFriday))

  const allPromotions = useDealPromotions(deal) as Promotion[] | null

  const hasUsps = !!allPromotions?.length

  const isP0 = !!(placementType === 'p0')
  const isP1 = placementType === 'p1'
  const isP4 = placementType === 'p4' && !(isTenancyUpdate || isP4Standard)

  const isColouredTenancy = isTenancyUpdate || (isBlackFriday && (isP1 || isP4))

  const isMobileView = asMobileCard || isP0

  const hasFeatures = isTenancyUpdate && !!deal?.features?.length

  const dataNerdEvents = []
  dataNerdEvents.push('viewed', 'loaded')
  if (isTvHomepage) dataNerdEvents.push('clicked')

  const dealObservedProps = isInitialRender ? {} : { 'data-nerd-x-observed': true }

  const dealSku = typeof segmentProps.product.sku === 'string' ? segmentProps.product.sku : ''

  const dealLinkProps = {
    deal,
    position,
    adType,
    placementType,
    href,
    onClick,
    target: '_blank',
    rel: 'nofollow noopener noreferrer',
    style: { textDecoration: 'none' },
    'data-nerd-events': JSON.stringify(dataNerdEvents),
    ...dealObservedProps,
    excludeNerdScript: !!dataNerdEvents.length,
    pageId,
    comparisonId: comparisonId || '',
    isClickout: true
  }

  const renderLink = (component: ReactNode) =>
    isTvHomepage ? <DealLink {...dealLinkProps}>{component}</DealLink> : component

  const detailBoxesOnClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation()
    event.preventDefault()

    const innerHtml = event.currentTarget.innerHTML
    setModalType(innerHtml)
    setIsDetailsModalOpen(true)

    if (innerHtml === 'Price details') {
      trackSegment('Element Clicked', {
        element_name: `Price details - SKU: ${dealSku}`,
        element_text: 'Price details',
        element_type: 'button'
      })
      return
    }

    if (innerHtml === 'Speed details') {
      trackSegment('Element Clicked', {
        element_name: `Speed details - SKU: ${dealSku}`,
        element_text: 'Speed details',
        element_type: 'button'
      })
    }
  }

  const { name: providerName, id: providerId } = provider
  const providerImage = generateProviderImage(providerName)

  const generateSpeedDetails = (deal: Deal, providerImage: string, providerName: string) => {
    const { downloadSpeed, uploadSpeed } = deal.services?.broadband || {}
    const isSymmetricalSpeed = downloadSpeed === uploadSpeed

    return {
      header: {
        heading: 'Speed details',
        image: {
          src: providerImage,
          alt: providerName,
          height: 40
        },
        title: deal.title
      },
      downloadSpeed: {
        title: 'Download speed',
        body: (
          <>
            Speed you <strong>receive data</strong> from the internet. It’s important for downloading files, streaming
            TV and movies online and loading websites.
          </>
        ),
        subTitle: 'Average download speed',
        speed: downloadSpeed
      },
      uploadSpeed: {
        title: 'Upload speed',
        body: (
          <>
            Speed you <strong>send data</strong> to the internet. It’s important for sharing files, posting photos and
            videos, making video calls and gaming online.
          </>
        ),
        subTitle: 'Average upload speed',
        speed: uploadSpeed
      },
      speedComparison: isSymmetricalSpeed
        ? {
            icon: HighSpeedIcon,
            title: 'Equal speeds',
            body: 'This package has equal download and upload speeds - or ‘symmetrical speeds’. That means you can upload as fast as you download with no speed caps!'
          }
        : null,
      smallPrint: [
        '*Average speeds are based on the speed available to at least 50% of customers during peak time (8 to 10pm) with this product.',
        'Your actual speeds may be higher or lower.'
      ]
    }
  }

  return (
    <ExpertPickWrapper
      author={deal?.expertPickContent?.author}
      productCallout={deal?.expertPickContent?.productCallout}
      isInExperiment={isMs3BannerTest}
    >
      <li className={styles.listItem} itemScope itemType='https://schema.org/Product'>
        <meta itemProp='url' content={href} />
        <meta itemProp='name' content={deal.title} />
        <meta itemProp='sku' content={dealSku} />

        {renderLink(
          <div
            className={classNames('dealcard', styles.container, {
              [styles.isP0]: isP0,
              [styles.isP4]: isP4,
              [styles.isMobileView]: isMobileView,
              [styles.hasUsps]: hasUsps,
              [styles.isTenancyUpdate]: isColouredTenancy,
              [styles.isBlackFriday]: isBlackFriday,
              [styles.hasFeatures]: hasFeatures,
              [styles.hasTrustScores]: showTrustpilotScore,
              [styles.hasPriceRiseBadges]: isMidContractPriceRiseTest,
              [skeletonStyles.dealcard]: isSkeleton
            })}
            data-cypress-id='deal-card'
            {...(!criticalJs && {
              role: 'link',
              onClick,
              onKeyUp: () => {}
            })}
            tabIndex={-1}
            {...props}
          >
            {isP4 && (
              <div className={styles.p4}>
                <Image imageUrl={tenancy?.display?.imageUrl || ''} />
              </div>
            )}

            <div className={styles.title}>
              <Title deal={deal} isExtensionShown={isExtensionShown} isMobileView={isMobileView} />
            </div>

            <div className={styles.detailBoxes}>
              <DetailBoxes
                deal={deal}
                isMobileView={isMobileView}
                openDetailsModal={detailBoxesOnClick}
                cityFibreDealsAvailable={cityFibreDealsAvailable}
              />
            </div>

            {hasFeatures && (
              <div className={styles.features}>
                <DealFeatures features={deal.features} />
              </div>
            )}

            {hasUsps && (
              <Fragment>
                <div className={`usps ${styles.usps}`}>
                  <Usps
                    promotionItems={allPromotions}
                    hasUspAnimated={hasUspAnimated}
                    setHasUspAnimated={setHasUspAnimated}
                    isHomepage={criticalJs}
                    isMobileView={isMobileView}
                    segmentProps={segmentProps}
                  />
                </div>

                {showTrustpilotScore && (
                  <div className={classNames(styles.trustpilot, { [styles.isP0]: isP0 })}>
                    <TrustpilotComponent
                      trustScores={deal.trustScores}
                      variant={'trustpilot-container--usp'}
                      isP0={isP0}
                    />
                  </div>
                )}
              </Fragment>
            )}

            {!hasUsps && showTrustpilotScore && (
              <div className={classNames(styles.trustpilot, { [styles.isP0]: isP0 })}>
                <TrustpilotComponent trustScores={deal.trustScores} isP0={isP0} />
              </div>
            )}

            <div className={styles.cta}>
              <Cta
                isMobileView={isMobileView}
                setIsMoreInfoOpen={setIsMoreInfoOpen}
                dealLinkProps={dealLinkProps}
                metadata={metadata}
                list={list}
                criticalJs={criticalJs}
                dealLinkRef={dealLinkRef}
                segmentProps={segmentProps}
              />
            </div>

            {isExtensionShown && (
              <Extension
                isExclusive={isExclusive}
                isSponsored={isSponsored}
                isEndingSoon={isEndingSoon}
                endDate={endDate ? new Date(endDate) : null}
                isMobileView={isMobileView}
                criticalJs={criticalJs}
                isBlackFriday={isBlackFriday}
                hasBlackFridayPromotion={isBlackFriday && hasBlackFridayPromotion}
                isTenancyUpdate={isColouredTenancy}
              />
            )}
          </div>
        )}

        <DetailsModal
          isOpen={isDetailsModalOpen}
          setOpen={() => setIsDetailsModalOpen(false)}
          modalType={modalType}
          content={
            modalType === 'Price details'
              ? { ...generatePriceDetails(deal, providerImage, providerName), providerId }
              : generateSpeedDetails(deal, providerImage, providerName)
          }
        />

        <TransitionOut isVisible={(!criticalJs || isP0) && isMoreInfoOpen} outDelay={300}>
          <MoreInfoSlideOut
            isOpen={isMoreInfoOpen}
            onClose={() => {
              setIsMoreInfoOpen(false)
            }}
            newCustomersOnly={customerAvailability === 'NEW' || !customerAvailability}
          >
            <DealCard
              deal={deal}
              tenancy={tenancy}
              list={list}
              position={position}
              adType={adType}
              placementType={isP0 ? '' : placementType}
              href={href}
              onClick={onClick}
              isTvHomepage={isTvHomepage}
              hasUspAnimated={hasUspAnimated}
              setHasUspAnimated={setHasUspAnimated}
              isSponsored={isSponsored}
              segmentProps={segmentProps}
              asMobileCard
            />

            {deal?.trustScores?.highlightReview && (
              <HighlightReview
                highlightReview={deal.trustScores.highlightReview}
                trustpilotUrl={deal.trustScores.trustpilotUrl}
              />
            )}

            <Tabs tabs={getContent(deal)} sku={segmentProps.product.sku} />
          </MoreInfoSlideOut>
        </TransitionOut>
      </li>
    </ExpertPickWrapper>
  )
}

export default DealCard
